// DataExtractor - Handles extraction of check, category, and control item details
export class DataExtractor {
  constructor() {
    this.extractionCache = new Map();
    this.extractionStats = {
      totalExtractions: 0,
      cacheHits: 0,
      cacheMisses: 0,
      extractionMethods: {
        originalCheck: 0,
        hierarchy: 0,
        formStructure: 0,
        fallback: 0
      }
    };
  }

  // Extract all details in one call for efficiency
  extractAllDetails(checkData, checkId, controlItemData, controlItemId, categoryData, categoryId, auditFormData) {
    const cacheKey = `${categoryId}_${controlItemId}_${checkId}`;
    
    if (this.extractionCache.has(cacheKey)) {
      this.extractionStats.cacheHits++;
      return this.extractionCache.get(cacheKey);
    }
    
    this.extractionStats.cacheMisses++;
    this.extractionStats.totalExtractions++;
    
    const extractedData = {
      checkId,
      controlItemId,
      categoryId,
      checkDetails: this.extractCheckDetails(checkData, checkId, auditFormData),
      categoryDetails: this.extractCategoryDetails(categoryId, categoryData, auditFormData),
      controlItemDetails: this.extractControlItemDetails(controlItemId, controlItemData, auditFormData)
    };
    
    // Cache the result
    this.extractionCache.set(cacheKey, extractedData);
    
    return extractedData;
  }

  // Extract check details with multiple fallback strategies
  extractCheckDetails(checkData, checkId, auditFormData) {
    console.log(`        🔍 Extracting check details for check ${checkId}`);
    
    // Strategy 1: Use original check data if available
    if (checkData.originalCheck) {
      console.log('        ✅ Using originalCheck data');
      this.extractionStats.extractionMethods.originalCheck++;
      
      return {
        check_id: checkData.originalCheck.check_id || parseInt(checkId),
        check_code: checkData.originalCheck.check_code || checkData.originalCheck.description || `Check ${checkId}`,
        deduction_points: checkData.originalCheck.deduction_points || 5,
        measurement_unit: checkData.originalCheck.measurement_unit || '',
        source: 'originalCheck'
      };
    }
    
    // Strategy 2: Look in hierarchy
    if (auditFormData.hierarchy?.checks) {
      const hierarchyCheck = auditFormData.hierarchy.checks.find(c => 
        c.check_id.toString() === checkId.toString()
      );
      
      if (hierarchyCheck) {
        console.log('        ✅ Using hierarchy check data');
        this.extractionStats.extractionMethods.hierarchy++;
        
        return {
          check_id: hierarchyCheck.check_id,
          check_code: hierarchyCheck.check_code || hierarchyCheck.description || hierarchyCheck.check_code_it,
          deduction_points: hierarchyCheck.deduction_points || 5,
          measurement_unit: hierarchyCheck.measurement_unit || '',
          source: 'hierarchy'
        };
      }
    }
    
    // Strategy 3: Look in form structure
    if (auditFormData.data?.form_structure) {
      for (const category of auditFormData.data.form_structure) {
        if (category.fields) {
          const field = category.fields.find(f => f.id.toString() === checkId.toString());
          if (field) {
            console.log('        ✅ Using form structure field data');
            this.extractionStats.extractionMethods.formStructure++;
            
            return {
              check_id: field.id,
              check_code: field.label || field.description,
              deduction_points: this.extractPointsFromField(field),
              measurement_unit: field.measurement_unit || '',
              source: 'formStructure'
            };
          }
        }
      }
    }
    
    // Strategy 4: Enhanced fallback with smart guessing
    console.log('        ⚠️ Using enhanced fallback check data');
    this.extractionStats.extractionMethods.fallback++;
    
    return {
      check_id: parseInt(checkId),
      check_code: this.generateSmartCheckCode(checkId, checkData),
      deduction_points: this.guessDeductionPoints(checkData),
      measurement_unit: this.guessMeasurementUnit(checkData),
      source: 'fallback'
    };
  }

  // Extract category details with multiple strategies
  extractCategoryDetails(categoryId, categoryData, auditFormData) {
    console.log(`        🔍 Extracting category details for category ${categoryId}`);
    
    // Strategy 1: Use category data from cumulative
    if (categoryData?.categoryName) {
      console.log('        ✅ Using cumulative category data');
      
      return {
        category_id: parseInt(categoryId),
        category_name: categoryData.categoryName,
        category_code: categoryData.categoryCode || categoryData.categoryName,
        source: 'cumulative'
      };
    }
    
    // Strategy 2: Look in hierarchy
    if (auditFormData.hierarchy?.categories) {
      const category = auditFormData.hierarchy.categories.find(c => 
        c.category_id.toString() === categoryId.toString()
      );
      
      if (category) {
        console.log('        ✅ Using hierarchy category data');
        
        return {
          category_id: category.category_id,
          category_name: category.category_name,
          category_code: category.category_code,
          source: 'hierarchy'
        };
      }
    }
    
    // Strategy 3: Look in form structure
    if (auditFormData.data?.form_structure) {
      const category = auditFormData.data.form_structure.find(c => 
        c.categoryData?.category_id.toString() === categoryId.toString()
      );
      
      if (category) {
        console.log('        ✅ Using form structure category data');
        
        return {
          category_id: category.categoryData.category_id,
          category_name: category.categoryData.category_code,
          category_code: category.categoryData.category_code,
          source: 'formStructure'
        };
      }
    }
    
    // Strategy 4: Enhanced fallback
    console.log('        ⚠️ Using enhanced fallback category data');
    
    return {
      category_id: parseInt(categoryId),
      category_name: this.generateSmartCategoryName(categoryId, categoryData),
      category_code: `CAT_${categoryId}`,
      source: 'fallback'
    };
  }

  // Extract control item details with multiple strategies
  extractControlItemDetails(controlItemId, controlItemData, auditFormData) {
    console.log(`        🔍 Extracting control item details for item ${controlItemId}`);
    
    // Strategy 1: Use control item data from cumulative
    if (controlItemData?.controlItemName) {
      console.log('        ✅ Using cumulative control item data');
      
      return {
        control_item_id: parseInt(controlItemId),
        control_item_code: controlItemData.controlItemName,
        control_item_code_it: controlItemData.controlItemTranslation || controlItemData.controlItemName,
        source: 'cumulative'
      };
    }
    
    // Strategy 2: Look in hierarchy
    if (auditFormData.hierarchy?.control_items) {
      const controlItem = auditFormData.hierarchy.control_items.find(ci => 
        ci.control_item_id.toString() === controlItemId.toString()
      );
      
      if (controlItem) {
        console.log('        ✅ Using hierarchy control item data');
        
        return {
          control_item_id: controlItem.control_item_id,
          control_item_code: controlItem.control_item_code,
          control_item_code_it: controlItem.control_item_code_it,
          source: 'hierarchy'
        };
      }
    }
    
    // Strategy 3: Look in form structure
    if (auditFormData.data?.form_structure) {
      for (const category of auditFormData.data.form_structure) {
        if (category.fields) {
          const field = category.fields.find(f => 
            f.controlItemData?.control_item_id.toString() === controlItemId.toString()
          );
          
          if (field) {
            console.log('        ✅ Using form structure control item data');
            
            return {
              control_item_id: field.controlItemData.control_item_id,
              control_item_code: field.controlItemData.control_item_code,
              control_item_code_it: field.controlItemData.control_item_code_it,
              source: 'formStructure'
            };
          }
        }
      }
    }
    
    // Strategy 4: Enhanced fallback
    console.log('        ⚠️ Using enhanced fallback control item data');
    
    return {
      control_item_id: parseInt(controlItemId),
      control_item_code: this.generateSmartControlItemCode(controlItemId, controlItemData),
      control_item_code_it: this.generateSmartControlItemCode(controlItemId, controlItemData),
      source: 'fallback'
    };
  }

  // Helper method to extract points from field
  extractPointsFromField(field) {
    if (field.pointOptions && Array.isArray(field.pointOptions)) {
      // Find the highest point value that's not 0
      const maxPoints = field.pointOptions
        .map(opt => opt.points)
        .filter(points => points > 0)
        .sort((a, b) => b - a)[0];
      
      return maxPoints || 5;
    }
    
    // Check for other point-related properties
    if (field.deduction_points) return field.deduction_points;
    if (field.points) return field.points;
    if (field.maxPoints) return field.maxPoints;
    
    return 5; // Default fallback
  }

  // Smart generation methods for fallback scenarios
  generateSmartCheckCode(checkId, checkData) {
    // Try to extract meaningful information from check data
    if (checkData.comment && checkData.comment.length > 0) {
      const words = checkData.comment.split(' ').slice(0, 3).join(' ');
      return `Check ${checkId}: ${words}`;
    }
    
    if (checkData.label) {
      return checkData.label;
    }
    
    if (checkData.description) {
      return checkData.description;
    }
    
    return `Check ${checkId}`;
  }

  generateSmartCategoryName(categoryId, categoryData) {
    // Look for any meaningful category information
    if (categoryData?.description) return categoryData.description;
    if (categoryData?.name) return categoryData.name;
    if (categoryData?.title) return categoryData.title;
    
    // Generate based on category ID patterns
    const patterns = {
      '1': 'Mechanical Components',
      '2': 'Electrical Systems',
      '3': 'Safety Checks',
      '4': 'Performance Tests',
      '5': 'Documentation Review'
    };
    
    return patterns[categoryId] || `Category ${categoryId}`;
  }

  generateSmartControlItemCode(controlItemId, controlItemData) {
    // Look for any meaningful control item information
    if (controlItemData?.description) return controlItemData.description;
    if (controlItemData?.name) return controlItemData.name;
    if (controlItemData?.title) return controlItemData.title;
    if (controlItemData?.code) return controlItemData.code;
    
    return `Control Item ${controlItemId}`;
  }

  guessDeductionPoints(checkData) {
    // Try to infer deduction points from check data
    if (checkData.severity === 'high' || checkData.priority === 'high') return 10;
    if (checkData.severity === 'medium' || checkData.priority === 'medium') return 5;
    if (checkData.severity === 'low' || checkData.priority === 'low') return 2;
    
    // Check for keywords in comments
    if (checkData.comment) {
      const comment = checkData.comment.toLowerCase();
      if (comment.includes('critical') || comment.includes('major')) return 10;
      if (comment.includes('minor') || comment.includes('small')) return 2;
    }
    
    return 5; // Default
  }

  guessMeasurementUnit(checkData) {
    // Try to infer measurement unit from check data
    if (checkData.unit) return checkData.unit;
    if (checkData.measurement_unit) return checkData.measurement_unit;
    
    // Check for keywords in comments or labels
    const text = (checkData.comment || checkData.label || '').toLowerCase();
    
    if (text.includes('mm') || text.includes('millimeter')) return 'mm';
    if (text.includes('cm') || text.includes('centimeter')) return 'cm';
    if (text.includes('inch') || text.includes('"')) return 'inch';
    if (text.includes('kg') || text.includes('kilogram')) return 'kg';
    if (text.includes('lb') || text.includes('pound')) return 'lb';
    if (text.includes('°c') || text.includes('celsius')) return '°C';
    if (text.includes('°f') || text.includes('fahrenheit')) return '°F';
    if (text.includes('psi') || text.includes('pressure')) return 'psi';
    if (text.includes('rpm') || text.includes('rotation')) return 'rpm';
    if (text.includes('volt') || text.includes('v')) return 'V';
    if (text.includes('amp') || text.includes('current')) return 'A';
    if (text.includes('percent') || text.includes('%')) return '%';
    
    return ''; // No unit detected
  }

  // Batch extraction for multiple items
  batchExtractDetails(extractionRequests) {
    console.log(`🔄 Starting batch extraction for ${extractionRequests.length} items...`);
    
    const results = [];
    const startTime = Date.now();
    
    extractionRequests.forEach((request, index) => {
      try {
        const extracted = this.extractAllDetails(
          request.checkData,
          request.checkId,
          request.controlItemData,
          request.controlItemId,
          request.categoryData,
          request.categoryId,
          request.auditFormData
        );
        
        results.push({
          index,
          success: true,
          extracted
        });
        
      } catch (error) {
        results.push({
          index,
          success: false,
          error: error.message
        });
      }
    });
    
    const duration = Date.now() - startTime;
    const successCount = results.filter(r => r.success).length;
    
    console.log(`✅ Batch extraction completed: ${successCount}/${extractionRequests.length} successful in ${duration}ms`);
    
    return {
      results,
      summary: {
        totalRequests: extractionRequests.length,
        successfulExtractions: successCount,
        failedExtractions: extractionRequests.length - successCount,
        totalDuration: duration,
        averageDuration: duration / extractionRequests.length
      }
    };
  }

  // Advanced extraction with validation
  extractWithValidation(checkData, checkId, controlItemData, controlItemId, categoryData, categoryId, auditFormData) {
    const extracted = this.extractAllDetails(
      checkData, checkId, controlItemData, controlItemId, categoryData, categoryId, auditFormData
    );
    
    // Validate extracted data
    const validation = this.validateExtractedData(extracted);
    
    return {
      ...extracted,
      validation
    };
  }

  // Validate extracted data quality
  validateExtractedData(extractedData) {
    const issues = [];
    const warnings = [];
    
    // Check check details
    if (!extractedData.checkDetails.check_id || extractedData.checkDetails.check_id <= 0) {
      issues.push('Invalid check ID');
    }
    
    if (!extractedData.checkDetails.check_code || extractedData.checkDetails.check_code.trim() === '') {
      issues.push('Missing check code');
    }
    
    if (extractedData.checkDetails.deduction_points < 0) {
      issues.push('Negative deduction points');
    }
    
    if (extractedData.checkDetails.source === 'fallback') {
      warnings.push('Check details from fallback source');
    }
    
    // Check category details
    if (!extractedData.categoryDetails.category_id || extractedData.categoryDetails.category_id <= 0) {
      issues.push('Invalid category ID');
    }
    
    if (extractedData.categoryDetails.source === 'fallback') {
      warnings.push('Category details from fallback source');
    }
    
    // Check control item details
    if (!extractedData.controlItemDetails.control_item_id || extractedData.controlItemDetails.control_item_id <= 0) {
      issues.push('Invalid control item ID');
    }
    
    if (extractedData.controlItemDetails.source === 'fallback') {
      warnings.push('Control item details from fallback source');
    }
    
    return {
      isValid: issues.length === 0,
      quality: this.calculateDataQuality(extractedData, issues, warnings),
      issues,
      warnings
    };
  }

  // Calculate data quality score
  calculateDataQuality(extractedData, issues, warnings) {
    let score = 100;
    
    // Deduct points for issues
    score -= issues.length * 20;
    
    // Deduct points for warnings
    score -= warnings.length * 5;
    
    // Deduct points for fallback sources
    if (extractedData.checkDetails.source === 'fallback') score -= 10;
    if (extractedData.categoryDetails.source === 'fallback') score -= 10;
    if (extractedData.controlItemDetails.source === 'fallback') score -= 10;
    
    // Bonus points for rich data
    if (extractedData.checkDetails.measurement_unit) score += 5;
    if (extractedData.checkDetails.deduction_points > 0) score += 5;
    
    return Math.max(0, Math.min(100, score));
  }

  // Get extraction statistics
  getStats() {
    const totalMethods = Object.values(this.extractionStats.extractionMethods).reduce((sum, count) => sum + count, 0);
    
    return {
      ...this.extractionStats,
      cacheHitRate: this.extractionStats.totalExtractions > 0 ? 
        (this.extractionStats.cacheHits / (this.extractionStats.cacheHits + this.extractionStats.cacheMisses) * 100).toFixed(2) + '%' : '0%',
      methodDistribution: {
        originalCheck: totalMethods > 0 ? (this.extractionStats.extractionMethods.originalCheck / totalMethods * 100).toFixed(2) + '%' : '0%',
        hierarchy: totalMethods > 0 ? (this.extractionStats.extractionMethods.hierarchy / totalMethods * 100).toFixed(2) + '%' : '0%',
        formStructure: totalMethods > 0 ? (this.extractionStats.extractionMethods.formStructure / totalMethods * 100).toFixed(2) + '%' : '0%',
        fallback: totalMethods > 0 ? (this.extractionStats.extractionMethods.fallback / totalMethods * 100).toFixed(2) + '%' : '0%'
      }
    };
  }

  // Clear extraction cache
  clearCache() {
    const cacheSize = this.extractionCache.size;
    this.extractionCache.clear();
    console.log(`🧹 Extraction cache cleared (${cacheSize} items removed)`);
    return cacheSize;
  }

  // Reset extraction statistics
  resetStats() {
    this.extractionStats = {
      totalExtractions: 0,
      cacheHits: 0,
      cacheMisses: 0,
      extractionMethods: {
        originalCheck: 0,
        hierarchy: 0,
        formStructure: 0,
        fallback: 0
      }
    };
    console.log('📊 Extraction statistics reset');
  }

  // Export extraction data
  export() {
    return {
      exportedAt: new Date().toISOString(),
      stats: this.getStats(),
      cacheSize: this.extractionCache.size,
      cacheKeys: Array.from(this.extractionCache.keys()).slice(0, 10) // First 10 keys as sample
    };
  }

  // Reset extractor
  reset() {
    this.clearCache();
    this.resetStats();
    console.log('🔄 Data extractor reset complete');
  }

  // Advanced extraction with context awareness
  extractWithContext(checkData, checkId, controlItemData, controlItemId, categoryData, categoryId, auditFormData, context = {}) {
    const {
      preferredSources = ['originalCheck', 'hierarchy', 'formStructure', 'fallback'],
      includeMetadata = false,
      validateQuality = false
    } = context;
    
    // Temporarily modify extraction logic based on preferred sources
    const originalMethod = this.extractCheckDetails;
    
    if (preferredSources[0] !== 'originalCheck') {
      // Custom extraction order based on preferences
      console.log(`🎯 Using custom extraction order: ${preferredSources.join(' -> ')}`);
    }
    
    const extracted = this.extractAllDetails(
      checkData, checkId, controlItemData, controlItemId, categoryData, categoryId, auditFormData
    );
    
    if (includeMetadata) {
      extracted.metadata = {
        extractedAt: new Date().toISOString(),
        context,
        cacheUsed: this.extractionCache.has(`${categoryId}_${controlItemId}_${checkId}`)
      };
    }
    
    if (validateQuality) {
      extracted.qualityAssessment = this.validateExtractedData(extracted);
    }
    
    return extracted;
  }

  // Get extraction recommendations
  getExtractionRecommendations(auditFormData) {
    const recommendations = [];
    
    // Analyze audit form data structure
    if (!auditFormData.hierarchy) {
      recommendations.push({
        priority: 'high',
        type: 'missing_hierarchy',
        message: 'Audit form missing hierarchy data. This will force fallback extraction.',
        impact: 'High reliance on fallback methods, reduced data quality'
      });
    }
    
    if (!auditFormData.data?.form_structure) {
      recommendations.push({
        priority: 'medium',
        type: 'missing_form_structure',
        message: 'Audit form missing form structure data. Limited extraction strategies available.',
        impact: 'Reduced extraction options, potential data quality issues'
      });
    }
    
    // Analyze extraction statistics
    const stats = this.getStats();
    
    if (stats.extractionMethods.fallback > stats.extractionMethods.originalCheck) {
      recommendations.push({
        priority: 'medium',
        type: 'high_fallback_usage',
        message: 'High usage of fallback extraction methods detected.',
        impact: 'Data quality may be compromised. Consider improving audit form data structure.'
      });
    }
    
    if (parseFloat(stats.cacheHitRate) < 50) {
      recommendations.push({
        priority: 'low',
        type: 'low_cache_efficiency',
        message: 'Low cache hit rate detected.',
        impact: 'Performance may be suboptimal. Consider increasing cache size or improving data consistency.'
      });
    }
    
    return recommendations;
  }
}